import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Box, TextField, Typography } from "@mui/material";
import { usePageMeta } from "../../contexts/PageMetaContext";
import useApiCall from "../../hooks/useApiCall";
import LoadingSpinner from "../../components/LoadingSpinner";
import BottomActionBar from "../../components/BottomActionBar";
import TriggerPoints from "../../components/TriggerPoints";
import { getPersonalityGuidelines } from "../../constants/AIGuidelines";
import { getStringParamsFromObject } from "../../utils";
import { INDIGO, SLATE } from "../../constants/Colors";

const TEXTAREA_SX = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900], lineHeight: 1.6 },
};

export default function AiPersonality() {
  const tokens = useSelector((state) => state?.tokens?.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const selectedAgentSiteCode = useSelector((state) => state?.apps?.selectedAgentSiteCode);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const defaultGuidelines = currentApp?.client_type ? getPersonalityGuidelines(currentApp.client_type) : "";

  const buildInitialState = () => ({
    ai_personality_guidelines: {
      display_name: "Chatbot Personality",
      prompt_input_raw: defaultGuidelines,
      id: null,
      app_id: null,
    },
  });

  const [state, setState] = useState(buildInitialState);
  const [triggerPoint, setTriggerPoint] = useState(selectedAgentSiteCode || "");
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const originalRef = useRef(null);

  useEffect(() => {
    setPageMeta({ description: "Configure the personality and tone of your AI agent." });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentApp?.id) return;
    setLoading(true);
    const query = getStringParamsFromObject({
      app_id: currentApp.id,
      trigger_point: triggerPoint,
      types: "ai_personality_guidelines",
    });
    makeRequest(`/v1/controls?${query}`, { method: "GET", authTokens: tokens })
      .then((configs) => {
        let next = buildInitialState();
        if (Array.isArray(configs) && configs.length > 0) {
          configs.forEach((c) => {
            if (next[c.internal_name]) {
              next[c.internal_name] = { ...next[c.internal_name], prompt_input_raw: c.prompt_input_raw, id: c.id, app_id: c.app_id };
            }
          });
        }
        originalRef.current = next.ai_personality_guidelines.prompt_input_raw;
        setState(next);
        setIsDirty(false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentApp?.id, triggerPoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (type, value) => {
    setState((prev) => {
      const next = { ...prev, [type]: { ...prev[type], prompt_input_raw: value } };
      setIsDirty(originalRef.current === null || next.ai_personality_guidelines.prompt_input_raw !== originalRef.current);
      return next;
    });
  };

  const handleSave = async () => {
    const updatedConfigs = Object.keys(state)
      .filter((type) => state[type]?.prompt_input_raw)
      .map((type) => ({
        internal_name: type,
        display_name: state[type].display_name,
        prompt_input_raw: state[type].prompt_input_raw,
        app_id: currentApp?.id,
        trigger_point: triggerPoint,
        ...(state[type].id ? { id: state[type].id } : {}),
      }));

    if (!updatedConfigs.length) return;

    setSaving(true);
    try {
      await makeRequest(`/v1/controls?app_id=${currentApp?.id}`, {
        method: "PUT",
        body: { controls: updatedConfigs },
        authTokens: tokens,
      });
      originalRef.current = state.ai_personality_guidelines.prompt_input_raw;
      setIsDirty(false);
    } catch {
      // error toasted by useApiCall
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading AI Personality..." subMessage="Fetching personality configuration" />;

  return (
    <Box sx={{ width: "100%", pb: 10 }}>
      {/* Trigger point selector (only for app-level, not agent context) */}
      {!selectedAgentSiteCode && currentApp?.trigger_points?.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <TriggerPoints app={currentApp} onChange={setTriggerPoint} triggerPoint={triggerPoint} />
        </Box>
      )}

      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
        }}
      >
        <Box sx={{ px: 3, py: "11px", borderBottom: `1px solid ${SLATE[200]}`, bgcolor: SLATE[50] }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900] }}>
            Chatbot Personality
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], lineHeight: 1.5, mb: 2 }}>
            Guidelines for the chatbot on how to interact with customers.
          </Typography>
          <TextField
            multiline
            minRows={20}
            maxRows={40}
            fullWidth
            name="prompt_input_raw"
            value={state.ai_personality_guidelines.prompt_input_raw}
            onChange={(e) => handleChange("ai_personality_guidelines", e.target.value)}
            placeholder={`1. E.g. Speak with a warm, friendly tone,\n2. E.g. Make sure to greet customers warmly and offer help in a cheerful manner\n3. E.g. Address the user by their first name`}
            sx={TEXTAREA_SX}
          />
        </Box>
      </Box>

      <BottomActionBar
        disabledSave={!isDirty || saving}
        handleSave={handleSave}
        saving={saving}
      />
    </Box>
  );
}
